import { useRef, useState } from 'react'

import { F0Box, F0Button, F0Text } from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

const STORAGE_KEY = 'factorial:ai-onboarding-interest-banner-dismissed'

export const AiOnboardingInterestBanner = () => {
  const [isDismissed, setIsDismissed] = useLocalStorage<boolean>(STORAGE_KEY)
  const [showThanks, setShowThanks] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasTrackedViewRef = useRef(false)

  const isVisible = !isDismissed && !showThanks

  useEffect(() => {
    if (!isVisible || hasTrackedViewRef.current) {
      return
    }

    track(trackingEvents.processes.viewAiOnboardingBanner)
    hasTrackedViewRef.current = true
  }, [isVisible])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const handleInterestClick = () => {
    track(trackingEvents.processes.clickAiOnboardingBanner)
    setIsDismissed(true)
    setShowThanks(true)

    timerRef.current = setTimeout(() => {
      setShowThanks(false)
    }, 3000)
  }

  const handleDismiss = () => {
    track(trackingEvents.processes.dismissAiOnboardingBanner)
    setIsDismissed(true)
  }

  if (isDismissed && !showThanks) return null

  if (showThanks) {
    return (
      <F0Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        padding="lg"
        gap="lg"
        width="full"
        background="info"
        borderRadius="md"
        marginBottom="lg"
      >
        <F0Text variant="label" content={i18n.t('processes.ai_onboarding_banner.thanks')} />
      </F0Box>
    )
  }

  return (
    <F0Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      padding="lg"
      gap="lg"
      width="full"
      background="info"
      borderRadius="md"
      marginBottom="lg"
    >
      <F0Box display="flex" flexDirection="column" gap="xs" grow>
        <F0Text variant="label" content={i18n.t('processes.ai_onboarding_banner.title')} />
        <F0Text
          variant="description"
          content={i18n.t('processes.ai_onboarding_banner.description')}
        />
      </F0Box>
      <F0Box shrink={false}>
        <F0Button
          onClick={handleInterestClick}
          variant="default"
          label={i18n.t('processes.ai_onboarding_banner.cta')}
        />
      </F0Box>
      <F0Box shrink={false}>
        <F0Button variant="ghost" icon={Cross} hideLabel label="Dismiss" onClick={handleDismiss} />
      </F0Box>
    </F0Box>
  )
}
